import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Sparkles, Menu, X, Phone } from 'lucide-react';

export const MobileNav = ({ showGetStarted = true }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <>
      {/* Phone Bar */}
      <div className="bg-gradient-to-r from-[#5B21B6] to-[#8B5CF6] text-white py-2 px-4 flex items-center justify-center">
        <a 
          href="tel:678-665-4712" 
          className="inline-flex items-center gap-2 font-bold hover:text-[#D4AF37] transition-colors"
          data-testid="phone-bar"
        >
          <Phone className="w-4 h-4" />
          Call Karine: 678-665-4712
        </a>
      </div>
      
      <header className="bg-white/60 backdrop-blur-xl border-b border-purple-200/50 relative z-50">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 md:gap-4" data-testid="nav-logo">
            <motion.div 
              className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-gradient-to-br from-purple-500 via-amber-400 to-pink-500 flex items-center justify-center glow-purple"
              animate={{ rotate: 360 }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            >
              <Sparkles className="w-6 h-6 md:w-7 md:h-7 text-white" />
            </motion.div>
            <span className="font-mystical text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold" style={{ color: '#5B21B6' }}>Intuitive Matchmaker</span>
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-4 lg:gap-6">
            <Link 
              to="/services" 
              className="text-[#5B21B6] hover:text-[#D4AF37] transition-colors font-semibold"
              data-testid="nav-services"
            >
              Services
            </Link>
            <Link 
              to="/calculator" 
              className="text-[#5B21B6] hover:text-[#D4AF37] transition-colors font-semibold"
              data-testid="nav-calculator"
            >
              Calculator
            </Link>
            <Link 
              to="/contact" 
              className="text-[#5B21B6] hover:text-[#D4AF37] transition-colors font-semibold"
              data-testid="nav-contact"
            >
              Contact
            </Link>
            <Link 
              to="/testimonials" 
              className="text-[#5B21B6] hover:text-[#D4AF37] transition-colors font-semibold"
              data-testid="nav-testimonials"
            >
              Testimonials
            </Link>
            <Link 
              to="/blog" 
              className="text-[#5B21B6] hover:text-[#D4AF37] transition-colors font-semibold"
              data-testid="nav-blog"
            >
              Blog
            </Link>
            <Link 
              to="/about-numerology" 
              className="text-[#5B21B6] hover:text-[#D4AF37] transition-colors font-semibold"
              data-testid="nav-about-numerology"
            >
              About
            </Link>
            {showGetStarted && (
              <Link to="/contact" data-testid="nav-cta">
                <Button className="rounded-full btn-magic px-6 py-3 font-bold">
                  Contact Karine
                </Button>
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 rounded-full bg-white/80 backdrop-blur-sm border border-purple-200 text-[#5B21B6]"
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="md:hidden overflow-hidden"
            >
              <div className="flex flex-col py-4 space-y-3">
                <Link 
                  to="/services" 
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-[#5B21B6] hover:text-[#D4AF37] transition-colors font-semibold text-lg py-2 border-b border-purple-100"
                  data-testid="mobile-nav-services"
                >
                  Services
                </Link>
                <Link 
                  to="/calculator" 
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-[#5B21B6] hover:text-[#D4AF37] transition-colors font-semibold text-lg py-2 border-b border-purple-100"
                  data-testid="mobile-nav-calculator"
                >
                  Soulmate Calculator
                </Link>
                <Link 
                  to="/contact" 
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-[#5B21B6] hover:text-[#D4AF37] transition-colors font-semibold text-lg py-2 border-b border-purple-100"
                  data-testid="mobile-nav-contact"
                >
                  Contact
                </Link>
                <Link 
                  to="/testimonials" 
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-[#5B21B6] hover:text-[#D4AF37] transition-colors font-semibold text-lg py-2 border-b border-purple-100"
                  data-testid="mobile-nav-testimonials"
                >
                  Testimonials
                </Link>
                <Link 
                  to="/blog" 
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-[#5B21B6] hover:text-[#D4AF37] transition-colors font-semibold text-lg py-2 border-b border-purple-100"
                  data-testid="mobile-nav-blog"
                >
                  Blog
                </Link>
                <Link 
                  to="/about-numerology" 
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-[#5B21B6] hover:text-[#D4AF37] transition-colors font-semibold text-lg py-2 border-b border-purple-100"
                  data-testid="mobile-nav-about-numerology"
                >
                  About Numerology
                </Link>
                {showGetStarted && (
                  <Link to="/contact" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-cta">
                    <Button className="w-full rounded-full btn-magic px-8 py-4 text-lg font-bold mt-2">
                      Contact Karine
                    </Button>
                  </Link>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      </header>
    </>
  );
};

export default MobileNav;
